import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { TabContent, TabPane, Nav, NavItem, NavLink } from 'reactstrap';
import 'react-tabs/style/react-tabs.css';
import { useRouter } from 'next/router';
import { useIsMobile } from '../../style/constants';
import { Badge, Dropdown } from './_index';
import { DropdownOption } from './Dropdown';

interface TabItem {
  label: string;
  component?: React.ReactNode;
  onClick?: () => void;
  content?: string;
  filter?: () => void;
  badgeCount?: null | number;
  route?: string;
  key?: string;
}

interface CentralizedTabProps {
  fullWidth?: boolean;
  tabs: TabItem[];
  size?: 'md' | 'sm';
  badgeCount?: null | number;
  propsComponent?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  tabItemClassName?: string;
  propsComponentClassName?: string;
  tabContentclassName?: string;
  shouldClearQueryParams?: boolean;
  withroute?: boolean;
  isAllComponentLoadAtOneTime?: boolean;
  dropdownitemclassName?: string;
}

const CentralizedTab: React.FC<CentralizedTabProps> = ({
  propsComponent,
  tabs,
  size = 'md',
  fullWidth = false,
  style,
  className,
  tabItemClassName,
  propsComponentClassName,
  dropdownitemclassName,
  tabContentclassName,
  shouldClearQueryParams,
  withroute = false,
  isAllComponentLoadAtOneTime = false,
}) => {
  const [activeTab, setActiveTab] = useState('1');

  const isMobile = useIsMobile();
  const router = useRouter();

  const tabclassName = classNames({
    [`nav-custom-${size}`]: true,
    [`nav-custom-tab-full-width`]: fullWidth,
  });

  const dropdownitemclassNameContainer = classNames(dropdownitemclassName, {
    [`dropdown-items-custom-style`]: true,
  });

  // const propsComponentClassName = classNames({
  //   [`propscomponent-custom-style`]: true,
  // });

  const options = tabs.map((tab, index) => ({
    value: `${index + 1}`,
    label: tab.label,
    ...(tab.badgeCount && { badgeCount: tab.badgeCount }),
  }));

  useEffect(() => {
    if (withroute && router.query) {
      const { tab } = router.query;
      const selectedTab = tabs.find((f) => f.route === tab);

      setActiveTab(selectedTab?.key || '1');
    }
  }, [router.query]);

  const routeManage = (routeData: string) => {
    if (shouldClearQueryParams) {
      router.replace(
        { pathname: router.pathname, query: undefined },
        undefined,
        { shallow: true },
      );
    }

    if (withroute) {
      router.replace(
        { pathname: router.pathname, query: { tab: routeData } },
        undefined,
        { shallow: true },
      );
    }
  };

  const handleSelectChange = (
    selected: DropdownOption | DropdownOption[] | null,
  ) => {
    if (selected !== null && !Array.isArray(selected)) {
      setActiveTab(selected.value.toString());
      const selectedTab = tabs.find((tab) => tab.label === selected.label);

      if (selectedTab && selectedTab.onClick) {
        selectedTab.onClick();
      }

      if (selectedTab && selectedTab.route) routeManage(selectedTab.route);
    }
  };

  const handleTabClick = (tabIndex: number) => {
    const selectedTab = tabs[tabIndex];
    setActiveTab(`${tabIndex + 1}`);

    if (selectedTab.route) routeManage(selectedTab.route);
  };

  return (
    <>
      {isMobile && tabs.length > 2 ? (
        <div className={className}>
          <Dropdown
            className={dropdownitemclassNameContainer}
            defaultValue={options[parseInt(activeTab, 10) - 1]}
            options={options}
            onChange={handleSelectChange}
          />

          <div className={propsComponentClassName}> {propsComponent}</div>
        </div>
      ) : (
        <div style={style} className={className}>
          <div className={tabItemClassName}>
            <Nav tabs fill className={tabclassName}>
              {tabs.map((tab, index) => (
                <NavItem key={`centralizetab-${index + 1}`}>
                  <NavLink
                    active={activeTab === `${index + 1}`}
                    onClick={() => {
                      handleTabClick(index);

                      if (tab.onClick) {
                        tab.onClick();
                      }
                    }}
                  >
                    {tab.label}
                    <>
                      {tab.badgeCount ? (
                        <Badge
                          text={tab.badgeCount ? tab.badgeCount.toString() : ''}
                        />
                      ) : (
                        <></>
                      )}
                    </>
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </div>
          <div className={propsComponentClassName}> {propsComponent}</div>
        </div>
      )}

      <TabContent activeTab={activeTab} className={tabContentclassName}>
        {tabs.map((tab, index) => (
          <TabPane key={`centralizetab-${index + 1}`} tabId={`${index + 1}`}>
            {isAllComponentLoadAtOneTime ? (
              <>
                {
                  tab.component /* render component if exists */
                    ? activeTab && tab.component
                    : activeTab &&
                      tab.content /* render content if component doesn't exist */
                }
              </>
            ) : (
              <>
                {Number(activeTab) === index + 1 ? (
                  <>
                    {
                      tab.component /* render component if exists */
                        ? activeTab && tab.component
                        : activeTab &&
                          tab.content /* render content if component doesn't exist */
                    }
                  </>
                ) : (
                  <></>
                )}
              </>
            )}
          </TabPane>
        ))}
      </TabContent>
    </>
  );
};

export default CentralizedTab;
